import { CloseIcon, SearchIcon } from "@src/assets";
import { Navigation, PageLayout, TextInput } from "@src/components";
import {
  forwardRef,
  HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GroupedVirtuoso, Virtuoso } from "react-virtuoso";
import { TokenItem } from "./TokenItem";
import { useSwapContext } from "@src/contexts";
import { useLocation, useNavigate } from "react-router-dom";
import { CASH_TOKEN_ADDRESS_BY_CHAIN } from "@src/config";
import { formatTokenValue, includesIgnoreCase } from "@src/utils";
import { RoutePath } from "@src/interfaces";

export const TokenPickerView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tokenAddress, excludeCash, excludeZeroBalanceTokens } =
    location.state || {};
  const [searchValue, setSearchValue] = useState("");
  const tokenSearchRef = useRef<HTMLInputElement | null>(null);
  const {
    allTokens,
    setToken,
    setTokenAmount,
    tokensBalances,
    isFetchingBalances,
    tokensPrices,
    setTransferToken,
    setChain,
    chain,
    supportedChainsMap,
    isMultichain,
  } = useSwapContext();

  const filteredAllTokens = useMemo(() => {
    const usdcAddressees = Object.values(CASH_TOKEN_ADDRESS_BY_CHAIN);

    return allTokens?.filter((token) => {
      if (excludeCash && usdcAddressees.includes(token.address.toLowerCase())) {
        return false;
      }
      return (
        includesIgnoreCase(token.symbol, searchValue) ||
        includesIgnoreCase(token.name, searchValue) ||
        token.address.toLowerCase() === searchValue.toLowerCase()
      );
    });
  }, [searchValue, allTokens, excludeCash]);

  const sortedTokensWithValues = useMemo(() => {
    return filteredAllTokens
      .map((token) => {
        const balance = tokensBalances[token.chainId]?.[token.address];
        const price = tokensPrices[token.chainId]?.[token.address];
        const value = formatTokenValue(balance?.wei, price, token.decimals);
        return { ...token, balance, value };
      })
      .filter((token) => {
        if (excludeZeroBalanceTokens) {
          const wei = token.balance?.wei ?? "0";
          return !(wei === "0");
        }
        return true;
      })
      .sort((a, b) => {
        const valueB = Number(b.value || 0);
        const valueA = Number(a.value || 0);
        if (valueB !== valueA) {
          return valueB - valueA;
        }
        if (b.chainlinkFunctionSupport !== a.chainlinkFunctionSupport) {
          if (b.chainlinkFunctionSupport) {
            return 1;
          } else if (a.chainlinkFunctionSupport) {
            return -1;
          }
        }
        return b.priority - a.priority;
      });
  }, [
    filteredAllTokens,
    tokensBalances,
    tokensPrices,
    excludeZeroBalanceTokens,
  ]);

  const [groupLabels, groupedTokens] = useMemo(() => {
    const currentChainTokens = sortedTokensWithValues.filter(
      (t) => t.chainId === chain?.chainId,
    );

    const otherTokens = sortedTokensWithValues.filter(
      (t) => t.chainId !== chain?.chainId,
    );

    const labels: string[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const groups: any[] = []; // TODO: Why is this any?

    if (currentChainTokens.length > 0) {
      labels.push("Current Network");
      groups.push(currentChainTokens);
    }

    if (otherTokens.length > 0) {
      labels.push("Other Networks");
      groups.push(otherTokens);
    }

    return [labels, groups];
  }, [sortedTokensWithValues, chain]);

  const flattenedGroups = groupedTokens.flat();

  useEffect(() => {
    if (tokenSearchRef.current) {
      setTimeout(() => {
        tokenSearchRef.current?.focus();
      }, 10); // delay focus a tiny bit to ensure it works
    }
  }, []);

  return (
    <PageLayout
      navigation={
        <Navigation
          leftItems={[
            {
              content: "Pick token",
              disabled: true,
              path: RoutePath.PickToken,
            },
          ]}
          rightItems={[
            {
              content: <CloseIcon />,
              onClick: () => navigate(-1),
            },
          ]}
        />
      }
    >
      <div className="flex flex-col gap-4 h-full">
        <div className="bg-card_bg rounded-xl px-4">
          <TextInput
            ref={tokenSearchRef}
            className="h-10"
            inputIcon={<SearchIcon />}
            placeholder="Search token"
            value={searchValue}
            handleChange={setSearchValue}
            // NOTE: used in e2e tests — do not remove
            dataTestId="token-picker-search-input"
          />
        </div>
        <div className="flex flex-col mx-0 h-full">
          {isMultichain ? (
            <GroupedVirtuoso
              className="h-full"
              groupCounts={groupedTokens.map((g) => g.length)}
              {...(isMultichain
                ? {
                    groupContent: (index: number) => (
                      <div className="flex flex-col gap-2 px-4 text-sm font-medium text-x-purple-500 text-opacity-60 bg-background_primary">
                        {groupLabels[index]}
                        <div className="w-full h-[1px] bg-text_primary/10" />
                      </div>
                    ),
                  }
                : {})}
              itemContent={(index) => {
                const tokenData = flattenedGroups[index];
                return (
                  tokenData && (
                    <TokenItem
                      token={tokenData}
                      isFetchingBalances={isFetchingBalances}
                      selectedTokenAddress={tokenAddress}
                      currentChain={chain}
                      onClick={() => {
                        setTokenAmount("");
                        setToken(tokenData);
                        setTransferToken(tokenData);
                        setChain(supportedChainsMap[tokenData.chainId]);
                        navigate(-1);
                      }}
                    />
                  )
                );
              }}
              components={{
                List: forwardRef<
                  HTMLDivElement,
                  HTMLAttributes<HTMLDivElement>
                >(({ style, ...props }, ref) => (
                  <div
                    ref={ref}
                    style={{
                      ...style,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                    {...props}
                  />
                )),
              }}
            />
          ) : (
            <Virtuoso
              className="h-full"
              totalCount={sortedTokensWithValues.length}
              itemContent={(index) => {
                const tokenData = sortedTokensWithValues[index];
                return (
                  tokenData && (
                    <TokenItem
                      token={tokenData}
                      isFetchingBalances={isFetchingBalances}
                      selectedTokenAddress={tokenAddress}
                      currentChain={chain}
                      onClick={() => {
                        setToken(tokenData);
                        setTransferToken(tokenData);
                        setChain(supportedChainsMap[tokenData.chainId]);
                        navigate(-1);
                      }}
                    />
                  )
                );
              }}
              components={{
                List: forwardRef<
                  HTMLDivElement,
                  HTMLAttributes<HTMLDivElement>
                >(({ style, ...props }, ref) => (
                  <div
                    ref={ref}
                    style={{
                      ...style,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                    {...props}
                  />
                )),
              }}
            />
          )}
        </div>
      </div>
    </PageLayout>
  );
};
